/**
 * ElevationDiagnostic — ELV-4 surface that answers "where did this
 * elevation number come from, and when was it fetched?".
 *
 * Pack: docs/prompt-packs/Elevation_Value_Bridge_Prompt.md
 *
 * ELV-1..3 shipped the resolver, the refresh CTA, and the at-gen-
 * time stamping. Live-prod walkthrough on Davenport revealed two
 * remaining gaps:
 *
 *   1. The Property → Site Boundary accordion shows a value (or
 *      "Data unavailable") but no signal about WHICH resolver tier
 *      produced it — polygon sample, USGS EPQS centroid fallback,
 *      legacy-zero-triple ⇒ no fetch, etc.
 *
 *   2. After a Refresh fires and returns
 *      "ELEVATION FETCH RETURNED NO USABLE SAMPLES" the user has
 *      no surface for "your last successful fetch was 12 minutes
 *      ago, here's the source" — only a destructive toast.
 *
 * This component renders below the elevation cells (or alongside
 * the empty-state) and surfaces:
 *   - source provenance (one of 4 tags)
 *   - last refreshed timestamp (epoch-ms → "5 min ago" / "2 hr ago" /
 *     date string), when available
 *   - a stable testid keyed on the source so e2e checks can pin
 *     "Davenport now reads polygon-sample" without scraping the
 *     human label
 *
 * Pure presentational component — no state, no side-effects. The
 * caller resolves the elevation via `resolveSiteElevation` and
 * passes the result + the site-level `refreshedAt` timestamp.
 *
 * Composability:
 *   - ELV-1 (`resolveSiteElevation`) — source tag comes from the
 *     resolver's `source` field.
 *   - ELV-2 (`refreshSiteElevation`) — `refreshedAt` is the epoch-
 *     ms the refresh writer should stamp on
 *     `SiteEntity.siteElevationRange.refreshedAt`.
 *   - ELV-3 (`generateReportFromSed` stamping) — for legacy reports
 *     whose `siteElevationSource === 'legacy-unknown'`, the
 *     diagnostic renders an explicit "Source not recorded" chip
 *     plus the Refresh CTA so the user can upgrade in-place.
 *   - PRR (shipped, 0.99201) — telemetry buckets match the
 *     `[provider:elevation]` pattern; this surface complements the
 *     server-side logs by surfacing the same provenance client-side.
 */
import React from 'react';
import type { ElevationSource } from '../../../lib/resolveSiteElevation';

/* ── Source tag display ─────────────────────────────────────────── */

const SOURCE_LABEL: Record<ElevationSource, string> = {
  'polygon-sample': 'Polygon sample (Google Elevation)',
  'usgs-epqs-centroid': 'USGS EPQS centroid',
  'google-centroid': 'Google centroid sample',
  'legacy-unknown': 'Source not recorded',
};

const SOURCE_HINT: Record<ElevationSource, string> = {
  'polygon-sample':
    'Up to 10 vertex + centroid samples across the parcel boundary.',
  'usgs-epqs-centroid':
    'Free USGS Elevation Point Query at the parcel centroid; US coverage only.',
  'google-centroid':
    'Google Elevation API single-point lookup at the parcel centroid.',
  'legacy-unknown':
    'This site was populated before provenance was recorded. Refresh elevation to recover.',
};

/* ── Last refreshed formatter ───────────────────────────────────── */

/**
 * Stable, locale-stable "N ago" formatter. Returns short-form
 * strings (`'12 sec ago'`, `'5 min ago'`, `'2 hr ago'`, `'3 days ago'`)
 * for recent timestamps and an ISO-date for anything older than 14
 * days. Pure — given the same `nowMs` and `refreshedAt`, returns
 * the same string.
 *
 * `nowMs` is parameterised so tests can pin a deterministic clock.
 */
export function formatRefreshedAt(
  refreshedAt: number | undefined,
  nowMs: number = Date.now(),
): string | null {
  if (refreshedAt == null || !Number.isFinite(refreshedAt) || refreshedAt <= 0) {
    return null;
  }
  const deltaMs = nowMs - refreshedAt;
  if (deltaMs < 0) {
    // Future timestamp (clock skew) — render the ISO date defensively.
    return new Date(refreshedAt).toISOString().slice(0, 10);
  }
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return `${sec} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day} day${day === 1 ? '' : 's'} ago`;
  return new Date(refreshedAt).toISOString().slice(0, 10);
}

/* ── Component ──────────────────────────────────────────────────── */

export interface ElevationDiagnosticProps {
  /** Resolver output. `null` means "no elevation available at all"
   *  — the component renders the empty-state hint + the Refresh CTA
   *  caller-side. */
  source: ElevationSource | null;
  /** Optional cascade stage identifier from `siteElevationRange.cascadeSource`. */
  cascadeSource?: string;
  /** Optional sample count from `siteElevationRange.pointsSampled`. */
  pointsSampled?: number;
  /** Epoch-ms from `SiteEntity.siteElevationRange.refreshedAt`.
   *  `undefined` ⇒ render the diagnostic without the "last refreshed"
   *  chip (legacy sites that never refreshed since ELV-4). */
  refreshedAt?: number;
  /** Optional clock override for deterministic testing. */
  nowMs?: number;
  /** Optional className passthrough so the host can override spacing. */
  className?: string;
}

export const ElevationDiagnostic: React.FC<ElevationDiagnosticProps> = ({
  source,
  cascadeSource,
  pointsSampled,
  refreshedAt,
  nowMs,
  className,
}) => {
  if (source == null) {
    return (
      <div
        data-testid="elevation-diagnostic-empty"
        className={
          className ??
          'mt-2 text-[10px] uppercase tracking-widest text-text-muted'
        }
      >
        Elevation source unavailable – Refresh to fetch.
      </div>
    );
  }
  const isLocal3DepGrid =
    source === 'polygon-sample'
    && typeof cascadeSource === 'string'
    && /^refdata-3dep/i.test(cascadeSource);
  const isGooglePolygonSampleWithDetails =
    source === 'polygon-sample'
    && !isLocal3DepGrid
    && ((typeof pointsSampled === 'number' && pointsSampled > 0)
      || (typeof cascadeSource === 'string' && cascadeSource.trim().length > 0));
  const label = isLocal3DepGrid
    ? `USGS 3DEP 1-arc-second grid summary stats (${cascadeSource}${typeof pointsSampled === 'number' && pointsSampled > 0 ? `, n=${pointsSampled}` : ''}; per-point coordinates/datum & slope-proxy derivation unverified/not retained in stored payload)`
    : isGooglePolygonSampleWithDetails
      ? `Polygon sample (Google Elevation${cascadeSource ? ` · ${cascadeSource}` : ''}${typeof pointsSampled === 'number' && pointsSampled > 0 ? ` · n=${pointsSampled} stored boundary sample` : ''}; report-level API call receipt not recorded)`
      : SOURCE_LABEL[source];
  const hint = SOURCE_HINT[source];
  const lastRefreshed = formatRefreshedAt(refreshedAt, nowMs);
  return (
    <div
      data-testid={`elevation-diagnostic-${source}`}
      className={
        className ??
        'mt-2 flex items-baseline gap-2 text-[10px] uppercase tracking-widest text-text-muted'
      }
    >
      <span
        className="font-bold text-text-secondary shrink-0 whitespace-nowrap"
        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        title={hint}
      >
        Source:
      </span>
      <span className="min-w-0">{label}</span>
      {lastRefreshed && (
        <>
          <span aria-hidden="true">·</span>
          <span
            data-testid="elevation-diagnostic-refreshed"
            title={new Date(refreshedAt!).toISOString()}
          >
            last refreshed {lastRefreshed}
          </span>
        </>
      )}
    </div>
  );
};

ElevationDiagnostic.displayName = 'ElevationDiagnostic';
